import {
  Box,
  Button,
  Flex,
  Text,
  ButtonGroup,
  Grid,
  GridItem,
  Textarea,
  Input,
  VisuallyHidden,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Select,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  AlertIcon,
  Alert,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IoChevronBackCircleSharp } from "react-icons/io5";
import {
  getAllAuditors,
  getApplicationById,
  getRenewApplicationById,
  postAssignToMe,
  saveAllAudits,
} from "./PlannerSlice";
import { useParams } from "react-router";
import { unwrapResult } from "@reduxjs/toolkit";
import { useEffect } from "react";
import axios from "axios";
import moment from "moment/moment";
import isAllowed from "../../routing/isAllowed";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { DocumentViewer } from "../../Components/common/DocumentViewer";
import DOCUMENT_FROM from "../../Common/Constants";
import CustomAlertDialog from "../../Components/CustomAlertDialog";

function RCFormRenewal() {
  const [status, setStatus] = useState("idle");
  const [isLoading, setIsLoading] = useState(false);
  const [auditor, setAuditor] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [unitId, setUnitId] = useState("");
  const { id } = useParams();
  const [auditData, setAuditData] = useState([]);
  const [remarks, setRemarks] = useState([]);
  const [selectKey, setSelectKey] = useState(0);
  const [refetch, setRefetch] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const onClose = () => setIsOpen(false);

  const navigate = useNavigate();
  const { userData } = useSelector((state) => state.auth);
  const applicationRenewStatus = "renew";

  const handleGoBack = () => navigate(-1);

  const token = localStorage.getItem("cu_user");

  const dispatch = useDispatch();
  const { renewApplication: application, entity } = useSelector(
    (state) => state.planner
  );

  const planSingleAudit = async (e) => {
    e.preventDefault();
    setSelectKey((prevKey) => prevKey + 1);
    const values = [
      {
        startDate: startDate,
        endDate: endDate,
        auditorId: auditor,
        unitId: unitId,
      },
    ];

    setRefetch(false);

    try {
      setIsLoading(true);

      if (
        startDate === "" &&
        endDate === "" &&
        auditor === "" &&
        unitId === ""
      ) {
        toast.error("Please select all required fields");
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_API_HOST}/api/cui-application-form/flow/v2/plan/RENEW/${application?._id}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setRefetch(true);
        setIsLoading(false);
        setAuditor("");
        setStartDate("");
        setEndDate("");
        setUnitId("");
        toast.success("Audit Saved successfully");
      }
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  const getList = async (id) => {
    try {
      setStatus("pending");
      setLoadError(null);
      const appResult = await dispatch(getRenewApplicationById(id));
      await dispatch(getAllAuditors());
      if (appResult?.payload) {
        setStatus("success");
      } else {
        setLoadError("Failed to load application. Please try again.");
        setStatus("failed");
      }
    } catch (error) {
      setLoadError(
        "Failed to load application. Token may have expired. Please refresh the page."
      );
      setStatus("failed");
      console.error(error);
    }
  };

  const handleRetry = () => {
    setRetrying(true);
    getList(encodeURIComponent(id));
    setTimeout(() => setRetrying(false), 1000);
  };

  const assignToMe = async (id) => {
    setIsLoading(true);
    try {
      setStatus("pending");
      await dispatch(postAssignToMe({ id, applicationRenewStatus }));
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error(error, "error");
    }
    navigate(-1);
  };

  const handleEndDateChange = (event) => {
    const selectedEndDate = event.target.value;
    if (selectedEndDate < startDate) {
      toast.info("End date cannot be before start date!");
      setEndDate("");
    } else {
      setEndDate(selectedEndDate);
    }
  };

  const handleSaveAllAudits = async (companyId) => {
    onClose();

    if (
      !application?.application_units?.every(
        (unit) => unit?.unit_audits?.length > 0
      )
    ) {
      toast.error("Cannot continue. There are no audit details for now.");
      return;
    }

    try {
      setStatus("pending");
      await dispatch(
        saveAllAudits({ companyId, applicationRenewStatus, type: "RENEW" })
      );
      setStatus("success");
    } catch (error) {
      setStatus("failed");
      console.error(error);
    }

    navigate(-1);
  };

  const [isListVisible, setIsListVisible] = useState(false);
  const toggleListVisibility = () => setIsListVisible(!isListVisible);

  useEffect(() => {
    getList(encodeURIComponent(id));
  }, [dispatch, id]);

  // Fixed: was [remarks] causing infinite re-render loop, changed to [application]
  useEffect(() => {
    const filteredRemarks = application?.remarks?.filter(
      (data) => data.comment
    );
    setRemarks(filteredRemarks);
  }, [application]);

  useEffect(() => {
    if (refetch) {
      getList(id);
    }
  }, [refetch]);

  // Shared text styles
  const labelProps = {
    color: "#141D43",
    fontSize: "md",
    fontWeight: "700",
    fontFamily: "font.roboto",
  };
  const valueProps = {
    color: "#464C64",
    fontSize: "sm",
    marginTop: "0.5rem",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  };

  return (
    <Box marginBottom={"8rem"}>
      {/* Error Message Display */}
      {loadError && status === "failed" && (
        <Box
          bg="red.50"
          border="1px solid"
          borderColor="red.200"
          borderRadius="md"
          p="4"
          mb="4"
          mx={{ base: "1rem", md: "2rem", lg: "3rem" }}
          mt={{ base: "1rem", md: "2rem", lg: "3rem" }}
          color="red.800"
        >
          <Flex justifyContent="space-between" alignItems="center">
            <Text>{loadError}</Text>
            <Button
              size="sm"
              colorScheme="red"
              variant="outline"
              onClick={handleRetry}
              isLoading={retrying}
            >
              Retry
            </Button>
          </Flex>
        </Box>
      )}
      {/* ── Header / Go Back ── */}
      <Box
        marginBottom={"2rem"}
        padding={{ base: "1rem", md: "2rem", lg: "3rem" }}
      >
        <Box
          display={"flex"}
          alignItems="center"
          cursor={"pointer"}
          onClick={handleGoBack}
        >
          <IoChevronBackCircleSharp color={"#52BBFF"} size={30} />
          &nbsp;
          <Text
            color={"#52BBFF"}
            fontSize={{ base: "sm", md: "sm", lg: "md" }}
            fontWeight={"800"}
            fontFamily={"font.roboto"}
          >
            Go Back
          </Text>
        </Box>

        <Box marginTop="2rem">
          <Text
            color={"#141D43"}
            fontSize={{ base: "xl", md: "2xl" }}
            fontWeight={"800"}
            fontFamily={"font.roboto"}
          >
            Registration Certificate
          </Text>
        </Box>

        {/* Gradient Banner */}
        <Box
          marginTop={"1.5rem"}
          color={"#fff"}
          padding={"10px 2rem"}
          fontWeight="700"
          marginLeft={{ base: "-1rem", md: "-2rem", lg: "-5rem" }}
          marginRight={{ base: "-1rem", md: "-2rem", lg: "-5rem" }}
          bg={
            "linear-gradient(90deg, rgba(81,187,255,1) 0%, rgba(81,187,255,1) 15%, rgba(164,206,255,1) 100%)"
          }
          borderTopRightRadius="100px"
          borderBottomRightRadius={"80px 80px"}
          fontSize={{ base: "sm", md: "md" }}
          wordBreak="break-word"
          overflowWrap="break-word"
        >
          Application Form &nbsp; {application?.id}
        </Box>

        {/* ── Applicant Details Card ── */}
        <Box
          bg="#fff"
          padding={{ base: "1rem", md: "2rem" }}
          marginTop="2rem"
          borderRadius={"15px"}
          overflow="hidden"
          maxW="100%"
        >
          {/* Assignment alert */}
          {application?.auditStatus !== "INCOMING" &&
          application?.auditStatus !== "COMPLETED" &&
          application?.auditStatus !== "RE-PLAN" ? (
            application?.applicationForm_userAssignedTo?._id !==
            userData?._id ? (
              <Box marginTop={"10px"} marginBottom="1rem">
                <Alert status="error" borderRadius={"10px"}>
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Please assign it to yourself</AlertTitle>
                    <AlertDescription>
                      You cannot approve the application as it is not assigned
                      to you.
                    </AlertDescription>
                  </Box>
                </Alert>
              </Box>
            ) : null
          ) : null}

          {/* Section: Applicant Details */}
          <Text
            color={"#141D43"}
            fontSize={{ base: "md", md: "lg" }}
            fontWeight={"800"}
            mb="1rem"
          >
            <Box
              as="span"
              borderRight={"3px solid #2272D5"}
              height="40px"
              marginRight={"1rem"}
            ></Box>
            Applicant Details
          </Text>

          <Box paddingLeft={{ base: "0.5rem", md: "3rem" }}>
            {/* Organisation Name */}
            <Box marginBottom={"1rem"}>
              <Text {...labelProps}>Name of Organisation</Text>
              <Text {...valueProps}>{application?.name || "N/A"}</Text>
            </Box>

            {/* Address */}
            <Box marginBottom={"1rem"}>
              <Text {...labelProps}>Address</Text>
              <Text {...valueProps}>
                {application?.address?.raw_address || "N/A"}
              </Text>
            </Box>

            {/* City / State */}
            <Grid
              templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
              gap={"1.5rem"}
              marginBottom={"1rem"}
            >
              <GridItem>
                <Text {...labelProps}>City</Text>
                <Text {...valueProps}>
                  {application?.address?.city || "N/A"}
                </Text>
              </GridItem>
              <GridItem>
                <Text {...labelProps}>State</Text>
                <Text {...valueProps}>
                  {application?.address?.state || "N/A"}
                </Text>
              </GridItem>
            </Grid>

            {/* Pincode / Country */}
            <Grid
              templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
              gap={"1.5rem"}
              marginBottom={"1rem"}
            >
              <GridItem>
                <Text {...labelProps}>Pincode</Text>
                <Text {...valueProps}>
                  {application?.address?.pinCode || "N/A"}
                </Text>
              </GridItem>
              <GridItem>
                <Text {...labelProps}>Country</Text>
                <Text {...valueProps}>
                  {application?.address?.country || "N/A"}
                </Text>
              </GridItem>
            </Grid>
          </Box>

          {/* Section: Legal Representative */}
          <Text
            color={"#141D43"}
            fontSize={{ base: "md", md: "lg" }}
            fontWeight={"800"}
            mb="5px"
          >
            Legal Representative
          </Text>
          <Box
            border={"2px solid #2272D5"}
            width="10%"
            marginBottom={"1rem"}
          ></Box>
          <Box paddingLeft={{ base: "0.5rem", md: "3rem" }}>
            <Grid
              templateColumns={{
                base: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              }}
              gap={"1.5rem"}
              marginBottom={"1rem"}
            >
              <GridItem>
                <Text {...labelProps}>Name</Text>
                <Text {...valueProps}>
                  {application?.legalRepresentative?.name || "N/A"}
                </Text>
              </GridItem>
              <GridItem>
                <Text {...labelProps}>Email</Text>
                <Text {...valueProps} wordBreak="break-all">
                  {application?.legalRepresentative?.email || "N/A"}
                </Text>
              </GridItem>
              <GridItem>
                <Text {...labelProps}>Contact</Text>
                <Text {...valueProps}>
                  {application?.legalRepresentative?.contact || "N/A"}
                </Text>
              </GridItem>
            </Grid>
          </Box>

          {/* Section: Point of Contact */}
          <Text
            color={"#141D43"}
            fontSize={{ base: "md", md: "lg" }}
            fontWeight={"800"}
            mb="5px"
          >
            Point of Contact
          </Text>
          <Box
            border={"2px solid #2272D5"}
            width="10%"
            marginBottom={"1rem"}
          ></Box>
          <Box paddingLeft={{ base: "0.5rem", md: "3rem" }}>
            <Grid
              templateColumns={{
                base: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              }}
              gap={"1.5rem"}
              marginBottom={"1rem"}
            >
              <GridItem>
                <Text {...labelProps}>Name</Text>
                <Text {...valueProps}>
                  {application?.contactPerson?.name || "N/A"}
                </Text>
              </GridItem>
              <GridItem>
                <Text {...labelProps}>Email</Text>
                <Text {...valueProps} wordBreak="break-all">
                  {application?.contactPerson?.email || "N/A"}
                </Text>
              </GridItem>
              <GridItem>
                <Text {...labelProps}>Contact</Text>
                <Text {...valueProps}>
                  {application?.contactPerson?.contact || "N/A"}
                </Text>
              </GridItem>
            </Grid>

            {/* Website */}
            <Box marginBottom={"1rem"}>
              <Text {...labelProps}>Website/Url</Text>
              <a
                href={`${application?.website}`}
                target="_blank"
                rel="noreferrer"
              >
                <Text {...valueProps} wordBreak="break-all">
                  {application?.website || "N/A"}
                </Text>
              </a>
            </Box>

            {/* Description */}
            <Box marginBottom={"1rem"}>
              <Text {...labelProps}>Brief Description about Organisation</Text>
              <Text {...valueProps}>{application?.description || "N/A"}</Text>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── RC Documents ── */}
      <Box
        marginTop="2rem"
        marginBottom="2rem"
        bg="#fff"
        borderRadius={"15px"}
        padding={{ base: "1rem", md: "2rem" }}
        mx={{ base: "1rem", md: "0" }}
        overflow="hidden"
        maxW="100%"
      >
        <Text
          color={"#141D43"}
          fontSize={{ base: "md", md: "lg" }}
          fontWeight={"800"}
          padding="0.5rem"
        >
          RC Documents
        </Text>

        {application?.company_document?.length > 0 ? (
          <DocumentViewer
            document={application?.company_document}
            referenceId={application?._id}
            doc_from={DOCUMENT_FROM.COMPANY}
          />
        ) : (
          <Box
            bg="#FFFFFF"
            w={{ base: "100%", sm: "175px" }}
            h={"125px"}
            borderRadius={"15px"}
            textAlign="center"
            display="flex"
            alignItems="center"
            mb="1rem"
            boxShadow={"rgba(100, 100, 111, 0.2) 0px 7px 29px 0px"}
          >
            <Text padding={"1rem"}>No Application Document Found</Text>
          </Box>
        )}
      </Box>

      {/* ── Plan Audits ── */}
      {application?.auditStatus !== "INCOMING" ? (
        <Box
          bg="#fff"
          padding={{ base: "1rem", md: "2rem" }}
          marginTop="2rem"
          marginX={{ base: "1rem", md: "0" }}
          borderRadius={"15px"}
          overflow="hidden"
          maxW="100%"
          pointerEvents={application?.assignedTo !== userData._id ? "none" : ""}
        >
          <Flex
            gap={{ base: "1rem", md: "15rem" }}
            flexDirection={{ base: "column", md: "row" }}
            alignItems={{ base: "flex-start", md: "center" }}
          >
            <Text
              color={"#141D43"}
              fontSize={{ base: "md", md: "lg" }}
              fontWeight={"800"}
              mb="5px"
            >
              Plan Audits
            </Text>
            {application?.auditStatus !== "COMPLETED" &&
            application?.applicationForm_userAssignedTo !== null &&
            application?.assignedTo !== userData._id ? (
              <Alert
                status="error"
                borderRadius={"10px"}
                height="auto"
                w={{ base: "100%", md: "50%" }}
              >
                <AlertIcon />
                <AlertDescription>
                  To perform any action, first assign this application.
                </AlertDescription>
              </Alert>
            ) : null}
          </Flex>

          <Box
            border={"2px solid #2272D5"}
            width="10%"
            marginBottom={"1rem"}
          ></Box>

          {application?.application_units?.length ? (
            application?.application_units?.map((unit) => (
              <form onSubmit={(e) => planSingleAudit(e)} key={unit?._id}>
                <Box
                  bg={"#FFFFFF"}
                  p={{ base: "1rem", md: "2rem" }}
                  mt={"5"}
                  boxShadow="rgba(100, 100, 111, 0.2) 0px 2px 10px 0px"
                  borderRadius="10px"
                  overflow="hidden"
                  pointerEvents={
                    application?.auditStatus === "INCOMING" ? "none" : ""
                  }
                >
                  {/* Top row: Facility, Address, Auditor, Dates */}
                  <Grid
                    templateColumns={{
                      base: "1fr",
                      sm: "repeat(2, 1fr)",
                      lg: "repeat(5, 1fr)",
                    }}
                    gap={{ base: "1rem", md: "1rem" }}
                  >
                    <GridItem>
                      <Text {...labelProps}>Facility Name</Text>
                      <Text {...valueProps}>{unit?.name || "N/A"}</Text>
                    </GridItem>

                    <GridItem>
                      <Text {...labelProps}>Address</Text>
                      <Text {...valueProps}>
                        {unit?.address?.raw_address || "N/A"}
                      </Text>
                    </GridItem>

                    <GridItem>
                      <Text {...labelProps}>Auditor</Text>
                      <Select
                        required
                        marginTop={"0.5rem"}
                        bgColor="#EDF3F8"
                        color={"#141D43"}
                        border="none"
                        fontSize="sm"
                        w={"100%"}
                        h={"53px"}
                        name="auditor"
                        onChange={(e) => setAuditor(e.target.value)}
                      >
                        <option hidden value="">
                          Select Auditor
                        </option>
                        {entity?.map((e) => (
                          <option
                            key={e?._id}
                            id={e?._id}
                            value={e?._id || application?.company_audit?.id}
                          >
                            {e?.name}
                          </option>
                        ))}
                      </Select>
                    </GridItem>

                    <GridItem>
                      <Text {...labelProps}>Start Date/Time</Text>
                      <Input
                        isRequired
                        name="startDate"
                        type="date"
                        color={"#141D43"}
                        marginTop="10px"
                        w="100%"
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </GridItem>

                    <GridItem>
                      <Text {...labelProps}>End Date/Time</Text>
                      <Input
                        isRequired
                        name="endDate"
                        type="date"
                        color={"#141D43"}
                        marginTop="10px"
                        w="100%"
                        min={startDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                          setUnitId(unit?._id);
                        }}
                      />
                    </GridItem>
                  </Grid>

                  {/* Second row: Type of Unit, Programs, Processes */}
                  <Grid
                    templateColumns={{
                      base: "1fr",
                      sm: "repeat(2, 1fr)",
                      md: "0.33fr 1fr 1fr",
                    }}
                    mt={"1rem"}
                    gap={{ base: "1rem", md: "2rem" }}
                  >
                    <GridItem>
                      <Text {...labelProps}>Type of Unit</Text>
                      <Text {...valueProps}>{unit?.unitType || "N/A"}</Text>
                    </GridItem>

                    <GridItem>
                      <Text {...labelProps}>Program(s) Selected</Text>
                      {unit?.unit_programs?.length > 0 ? (
                        <Menu>
                          <MenuButton
                            mt="1rem"
                            as={Button}
                            bg={"#EDF3F8"}
                            w={"100%"}
                            h={"43px"}
                          >
                            View Programs
                          </MenuButton>
                          <MenuList borderRadius={"15px"}>
                            {unit?.unit_programs?.map((p) => (
                              <MenuItem
                                key={p?.name}
                                bg="#EDF3F8"
                                mt={"1rem"}
                                w={"90%"}
                                h={"43px"}
                                borderRadius={"15px"}
                                textAlign={"center"}
                                justifyContent="center"
                                ml={"0.5rem"}
                              >
                                {p?.name}
                              </MenuItem>
                            ))}
                          </MenuList>
                        </Menu>
                      ) : (
                        <Text mt={"1rem"} textAlign={"center"}>
                          No programs available
                        </Text>
                      )}
                    </GridItem>

                    <GridItem>
                      <Text {...labelProps}>Type of Process</Text>
                      {unit?.unit_processes?.length > 0 ? (
                        <Menu>
                          <MenuButton
                            mt="1rem"
                            as={Button}
                            bg={"#EDF3F8"}
                            w={{ base: "100%", md: "50%" }}
                            h={"43px"}
                          >
                            View Processes
                          </MenuButton>
                          <MenuList borderRadius={"15px"}>
                            {unit?.unit_processes?.map((p) => (
                              <MenuItem
                                key={p?.name}
                                bg="#EDF3F8"
                                mt={"1rem"}
                                w={"90%"}
                                h={"43px"}
                                borderRadius={"15px"}
                                textAlign={"center"}
                                justifyContent="center"
                                ml={"0.5rem"}
                              >
                                {p?.name}
                              </MenuItem>
                            ))}
                          </MenuList>
                        </Menu>
                      ) : (
                        <Text mt={"1rem"} textAlign={"center"}>
                          No processes available
                        </Text>
                      )}
                    </GridItem>
                  </Grid>

                  {application?.applicationForm_userAssignedTo !== null && (
                    <Button
                      bg="#52BBFF"
                      color={"#ffffff"}
                      w={{ base: "100%", sm: "193px" }}
                      mt="1rem"
                      h="40px"
                      _hover={{ backgroundColor: "#52BBFF" }}
                      disabled={
                        isLoading ||
                        !auditor ||
                        !startDate ||
                        !endDate ||
                        !unitId
                      }
                      type="submit"
                    >
                      Save Audit
                    </Button>
                  )}
                </Box>

                {/* Audit history table */}
                <Box
                  bg="#fff"
                  key={selectKey}
                  borderRadius={"15px"}
                  mt="1rem"
                  boxShadow="rgba(100, 100, 111, 0.2) 0px 2px 10px 0px"
                  overflow="hidden"
                >
                  {/* Desktop header row */}
                  <Grid
                    padding={"1rem"}
                    mt={"2rem"}
                    templateColumns="repeat(4, 1fr)"
                    gap="0.5rem"
                    display={{ base: "none", md: "grid" }}
                  >
                    <GridItem>
                      <Text fontWeight={"700"}>Auditor</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontWeight={"700"}>Start Date Of Audit</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontWeight={"700"}>End Date of Audit</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontWeight={"700"}>Audit Status</Text>
                    </GridItem>
                  </Grid>

                  {unit?.unit_audits?.length ? (
                    unit?.unit_audits?.map((audit, idx) => (
                      <Box key={idx}>
                        {/* Mobile card */}
                        <Box
                          display={{ base: "block", md: "none" }}
                          padding={"1rem"}
                          borderBottom="1px solid #EDF3F8"
                        >
                          <Flex justifyContent="space-between" mb="0.25rem">
                            <Text fontSize="xs" color="#888">
                              Auditor
                            </Text>
                            <Text
                              fontSize="sm"
                              wordBreak="break-word"
                              textAlign="right"
                              maxW="60%"
                            >
                              {audit?.audit_auditor?.name || "N/A"}
                            </Text>
                          </Flex>
                          <Flex justifyContent="space-between" mb="0.25rem">
                            <Text fontSize="xs" color="#888">
                              Start
                            </Text>
                            <Text fontSize="sm">
                              {moment(audit?.startDate).format("DD-MM-YYYY")}
                            </Text>
                          </Flex>
                          <Flex justifyContent="space-between" mb="0.25rem">
                            <Text fontSize="xs" color="#888">
                              End
                            </Text>
                            <Text fontSize="sm">
                              {moment(audit?.endDate).format("DD-MM-YYYY")}
                            </Text>
                          </Flex>
                          <Flex justifyContent="space-between">
                            <Text fontSize="xs" color="#888">
                              Status
                            </Text>
                            <Text fontSize="sm">{audit?.status || "N/A"}</Text>
                          </Flex>
                        </Box>

                        {/* Desktop row */}
                        <Grid
                          display={{ base: "none", md: "grid" }}
                          padding={"1rem"}
                          templateColumns="repeat(4, 1fr)"
                          gap="1rem"
                        >
                          <GridItem>
                            <Text
                              wordBreak="break-word"
                              overflowWrap="break-word"
                            >
                              {audit?.audit_auditor?.name || "N/A"}
                            </Text>
                          </GridItem>
                          <GridItem>
                            <Text>
                              {moment(audit?.startDate).format("DD-MM-YYYY")}
                            </Text>
                          </GridItem>
                          <GridItem>
                            <Text>
                              {moment(audit?.endDate).format("DD-MM-YYYY")}
                            </Text>
                          </GridItem>
                          <GridItem>
                            <Text
                              wordBreak="break-word"
                              overflowWrap="break-word"
                            >
                              {audit?.status || "N/A"}
                            </Text>
                          </GridItem>
                        </Grid>
                      </Box>
                    ))
                  ) : (
                    <Text
                      textAlign={"center"}
                      fontWeight={"700"}
                      mt="0.5rem"
                      pb="1rem"
                    >
                      No details of audits for now
                    </Text>
                  )}
                </Box>

                <Box mt="2rem" h="5px" bg="gray.200" borderRadius="15px" />
              </form>
            ))
          ) : (
            <Text fontWeight={"600"} textAlign={"center"}>
              No planned audits for now
            </Text>
          )}
        </Box>
      ) : null}

      {/* ── Remarks (non-admin) ── */}
      {!userData?.role?.includes("ADMIN") ? (
        <Box width={"100%"} mt="1.5rem" px={{ base: "1rem", md: "0" }}>
          <Text {...labelProps} margin={"1rem"}>
            Remarks
          </Text>
          <TableContainer overflowX="auto">
            <Table
              variant={"simple"}
              size={{ base: "sm", md: "md" }}
              bg={"#fff"}
              borderRadius={"15px"}
            >
              <Thead>
                <Tr>
                  <Th color="brand.blue" textTransform="capitalize">
                    Sr no.
                  </Th>
                  <Th color="brand.blue" textTransform="capitalize">
                    Date Time
                  </Th>
                  <Th color="brand.blue" textTransform="capitalize">
                    Comment by
                  </Th>
                  <Th color="brand.blue" textTransform="capitalize">
                    Remarks
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {remarks?.length ? (
                  remarks.map((remark, index) => (
                    <Tr key={index}>
                      <Td>{index + 1}</Td>
                      <Td whiteSpace="nowrap">
                        {moment(remark?.time).format("DD-MM-YYYY hh:mm")}
                      </Td>
                      <Td>
                        {remark.userId &&
                          `${remark?.userId?.name} (${remark?.userId?.id})`}
                      </Td>
                      <Td>{remark?.comment || "N/A"}</Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan="4" textAlign="center" fontWeight={"700"}>
                      No remarks for now
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      ) : null}

      {/* ── Logs (admin) ── */}
      {userData?.role?.includes("ADMIN") ? (
        <Box width={"100%"} mt="1.5rem" px={{ base: "1rem", md: "0" }}>
          <Text {...labelProps} margin={"1rem"}>
            Logs
          </Text>
          <TableContainer overflowX="auto">
            <Table
              variant={"simple"}
              size={{ base: "sm", md: "md" }}
              bg={"#fff"}
              borderRadius={"15px"}
            >
              <Thead>
                <Tr>
                  <Th color="brand.blue" textTransform="capitalize">
                    Sr no.
                  </Th>
                  <Th color="brand.blue" textTransform="capitalize">
                    Date Time
                  </Th>
                  <Th color="brand.blue" textTransform="capitalize">
                    User
                  </Th>
                  <Th color="brand.blue" textTransform="capitalize">
                    Logs
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {application?.remarks?.length ? (
                  application?.remarks?.map((remark, index) => (
                    <Tr key={index}>
                      <Td>{index + 1}</Td>
                      <Td whiteSpace="nowrap">
                        {moment(remark?.time).format("DD-MM-YYYY hh:mm")}
                      </Td>
                      <Td>
                        {remark.userId &&
                          `${remark?.userId?.name} (${remark?.userId?.id})`}
                      </Td>
                      <Td>{remark?.comment || "N/A"}</Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan="4" textAlign="center" fontWeight={"700"}>
                      No logs for now
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      ) : null}

      {/* ── Bottom Action Buttons ── */}
      {application?.auditStatus === "COMPLETED" ? null : (
        <Box mt={"2rem"} px={{ base: "1rem", md: "0" }}>
          <Flex
            gap={2}
            flexDirection={{ base: "column", sm: "row" }}
            alignItems={{ base: "stretch", sm: "center" }}
          >
            {application?.applicationForm_userAssignedTo !== null && (
              <Box>
                {application?.assignedTo === userData._id ? (
                  <Button
                    bg="#52BBFF"
                    _hover={{ bg: "#52BBFF" }}
                    color={"#ffffff"}
                    w={{ base: "100%", sm: "193px" }}
                    h="50px"
                    disabled={isLoading ? true : ""}
                    onClick={() => setIsOpen(true)}
                  >
                    Save Details
                  </Button>
                ) : (
                  <Box
                    bg="brand.gray"
                    boxShadow={"rgba(100, 100, 111, 0.2) 0px 20px 50px 0px"}
                    w={{ base: "100%", sm: "300px" }}
                    h="auto"
                    minH="50px"
                    borderRadius={"15px"}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    p="0.5rem"
                  >
                    <Text
                      color={"#141D43"}
                      fontWeight={"800"}
                      fontFamily={"font.roboto"}
                      textAlign={"center"}
                      fontSize={{ base: "sm", md: "md" }}
                    >
                      Before performing any action, assign this application.
                    </Text>
                  </Box>
                )}
              </Box>
            )}

            {application?.assignedTo === userData._id ? (
              <Box
                bg="brand.gray"
                boxShadow={"rgba(100, 100, 111, 0.2) 0px 20px 50px 0px"}
                w={{ base: "100%", sm: "300px" }}
                h="50px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius={"15px"}
              >
                <Text
                  p={"5px"}
                  color={"#141D43"}
                  fontWeight={"800"}
                  fontFamily={"font.roboto"}
                  textAlign={"center"}
                  fontSize={{ base: "sm", md: "md" }}
                >
                  You are currently assigned to this application.
                </Text>
              </Box>
            ) : (
              <Button
                bg="#A8BF87"
                _hover={{ bg: "#A8BF87" }}
                color={"#ffffff"}
                w={{ base: "100%", sm: "193px" }}
                h="50px"
                disabled={isLoading ? true : ""}
                onClick={() => assignToMe(encodeURIComponent(id))}
              >
                Assign to me
              </Button>
            )}
          </Flex>
        </Box>
      )}

      <CustomAlertDialog
        isOpen={isOpen}
        onClose={onClose}
        title="Are you sure?"
        content="This action cannot be undone. Do you want to continue?"
        cancelButtonText="Cancel"
        confirmButtonText="Confirm"
        cancelButtonColor="gray"
        confirmButtonColor="#52BBFF"
        onConfirm={() => handleSaveAllAudits(id)}
      />
    </Box>
  );
}

export default isAllowed(RCFormRenewal);
